import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLink, Button, Reveal, SlotImage, usePageAsset } from "~/components/ui";
import { Tabs } from "~/components/ui/Tabs";
import { useMediaQuery } from "~/components/use-media-query";
import type { PageAssetSlot } from "~/lib/assets/slots";

/**
 * From the `lg` cut the explorer is tabs with one panel; below it, an accordion. The
 * accordion is the baseline — what the server emits and what a phone reads before
 * hydration — and the tablist is the enhancement the query switches on.
 */
const PHASE_EXPLORER_TABS_QUERY = "(min-width: 1024px)";

const PHASE_SLOTS = ["method-phase-1", "method-phase-2", "method-phase-3", "method-phase-4"] as const satisfies readonly PageAssetSlot[];

/** The `phases` namespace, one entry per phase — the source Home and Método share (T872). */
type PhaseCopy = {
  id: string;
  number: string;
  eyebrow: string;
  label: string;
  heading: string[];
  body: string;
  learn_label: string;
  learn: string;
  /** Método's own reading of the phase (T872 S4, `page-content.json` metodo): a longer
   *  body, its «Aprendes a», and how she moves on. The Home keeps the short copy above. */
  method?: { learn: string; body: string; advance: string };
};

const ID_PREFIX = "phase";

/**
 * T872 S3 — the four phases of Ritual Core, from the October delivery (`PhaseTab` +
 * `PhaseCard`, `exploration.js`): on a desktop a `role="tablist"` (the S2 `Tabs`, roving
 * tabindex, arrows and Home/End) whose one visible panel holds the phase's copy, «Aprendes
 * a», the session still and the link to the method; on a phone the same panels under
 * toggles, one open at a time and the open one closes on a second press. Phase 1 opens
 * first. Both controls are in the DOM — CSS picks one by the `lg` cut — so the first paint
 * needs no script; the media query only decides the panel's role and label owner, and the
 * server pass emits the accordion's (`region` labelled by its toggle), the phone's reading.
 *
 * Copy is the shared `phases` namespace; stills are the `method-phase-N` slots Método
 * already manages (an empty slot renders no figure).
 */
export function PhaseExplorer({ variant = "home" }: { variant?: "home" | "method" } = {}) {
  const { t } = useTranslation("phases");
  const method = variant === "method";
  const raw = t("items", { returnObjects: true });
  const phases = (Array.isArray(raw) ? raw : []) as PhaseCopy[];
  const stacked = !useMediaQuery(PHASE_EXPLORER_TABS_QUERY);
  const [current, setCurrent] = useState<string | null>(phases[0]?.id ?? null);
  const [opened, setOpened] = useState<readonly string[]>(() => phases[0] ? [phases[0].id] : []);
  const selectPhase = (id: string | null) => {
    setCurrent(id);
    if (id !== null) setOpened((ids) => ids.includes(id) ? ids : [...ids, id]);
  };
  // A tablist always has a selected tab; an accordion may be fully closed.
  const active = stacked ? current : (current ?? phases[0]?.id ?? null);

  if (phases.length === 0) return null;

  return (
    <div className={method ? "pub-phases is-method" : "pub-phases"}>
      <Tabs
        variant="phase"
        idPrefix={ID_PREFIX}
        ariaLabel={t("label")}
        value={active ?? phases[0].id}
        options={phases.map((phase) => ({ id: phase.id, label: phase.label, kicker: phase.number }))}
        onChange={selectPhase}
      />
      {phases.map((phase, index) => {
        const expanded = active === phase.id;
        const panelId = `${ID_PREFIX}-panel-${phase.id}`;
        const toggleId = `${ID_PREFIX}-toggle-${phase.id}`;
        return (
          <article className="pub-phase-card" key={phase.id}>
            <Button
              variant="unstyled"
              className="pub-phase-toggle"
              id={toggleId}
              aria-expanded={expanded}
              aria-controls={panelId}
              onClick={() => selectPhase(current === phase.id ? null : phase.id)}
            >
              <span className="pub-phase-tab-number">{phase.number}</span>
              <span className="pub-phase-toggle-name">{phase.label}</span>
              <span className="pub-phase-toggle-symbol" aria-hidden="true">
                {expanded ? "−" : "+"}
              </span>
            </Button>
            {/* The shared `Reveal` (2026-10-09): on a phone it opens by height, closed panels
                inert; on a desktop the `.pub-phase-panel` CSS turns it into a swap — `hidden`
                hides the other tabs' panels and the visible one fades in. One element for both
                layouts, so the server pass (the phone's) needs no script and a desktop before
                hydration still shows one phase. */}
            <Reveal
              className="pub-phase-panel"
              id={panelId}
              role={stacked ? "region" : "tabpanel"}
              aria-labelledby={stacked ? toggleId : `${ID_PREFIX}-tab-${phase.id}`}
              hidden={stacked ? undefined : !expanded}
              open={expanded}
            >
              <div className="pub-phase-copy">
                <p className="pub-eyebrow pub-phase-eyebrow">{phase.eyebrow}</p>
                <h3>
                  {(Array.isArray(phase.heading) ? phase.heading : [phase.label]).map((line, i) => (
                    <Fragment key={line}>
                      {i > 0 ? <br /> : null}
                      {line}
                    </Fragment>
                  ))}
                </h3>
                {method && phase.method ? (
                  <>
                    <div className="pub-phase-learning">
                      <p className="pub-eyebrow">{phase.learn_label}</p>
                      <p>{phase.method.learn}</p>
                    </div>
                    <p className="pub-phase-body">{phase.method.body}</p>
                    <div className="pub-phase-advance">
                      <p className="pub-phase-advance-label">
                        {index === phases.length - 1 ? t("continue_label") : t("advance_label")}
                      </p>
                      <p>{phase.method.advance}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="pub-phase-body">{phase.body}</p>
                    <div className="pub-phase-learning">
                      <p className="pub-eyebrow">{phase.learn_label}</p>
                      <p>{phase.learn}</p>
                    </div>
                  </>
                )}
              </div>
              {/* Text stays server-rendered; photos first mount when opened. Keep a visited
                  photo mounted so closing the height animation never loses its content. */}
              {expanded || opened.includes(phase.id) ? (
                <PhasePhoto slot={PHASE_SLOTS[index] ?? PHASE_SLOTS[0]} />
              ) : null}
              {method ? (
                <ArrowLink href="#practica" arrow="→" className="pub-phase-link">
                  {t("method_link")}
                </ArrowLink>
              ) : (
                <ArrowLink routeKey="metodo" arrow="→" className="pub-phase-link">
                  {t("link")}
                </ArrowLink>
              )}
            </Reveal>
          </article>
        );
      })}
    </div>
  );
}

/**
 * `heroDesktop` (1920 wide, `scale-down`) first, as Método does: the panel column is
 * ~700 CSS px and a 2x display shows the 768 variant upscaled. Gated on the variant's
 * URL, not on the record, so a derivative that never resolved leaves no grey box.
 */
function PhasePhoto({ slot }: { slot: (typeof PHASE_SLOTS)[number] }) {
  const asset = usePageAsset(slot);
  const variant = asset?.imageUrls?.heroDesktop ? "heroDesktop" : asset?.imageUrls?.heroMobile ? "heroMobile" : null;
  if (!variant) return null;
  return (
    <figure className="pub-phase-photo">
      <SlotImage slot={slot} variant={variant} loading="lazy" />
    </figure>
  );
}
